import React from 'react';
import { CDIsotype } from './BrandMark';
export type ProductScene = 'pack' | 'toolkit' | 'templates' | 'guide';
type ProductCoverProps = {
  title: string;
  kicker: string;
  scene: ProductScene;
  variant?: 'card' | 'featured';
};
const SCENE_TINT: Record<ProductScene, string> = {
  pack: 'radial-gradient(60% 60% at 26% 14%, rgba(37,99,246,0.16), transparent 66%), radial-gradient(60% 60% at 80% 88%, rgba(122,77,255,0.16), transparent 66%)',
  toolkit: 'radial-gradient(60% 60% at 74% 16%, rgba(122,77,255,0.16), transparent 66%), radial-gradient(60% 60% at 18% 86%, rgba(37,99,246,0.14), transparent 66%)',
  templates: 'radial-gradient(60% 60% at 50% 6%, rgba(37,99,246,0.16), transparent 62%), radial-gradient(55% 55% at 86% 92%, rgba(122,77,255,0.14), transparent 66%)',
  guide: 'radial-gradient(60% 60% at 20% 20%, rgba(122,77,255,0.14), transparent 66%), radial-gradient(65% 65% at 88% 82%, rgba(37,99,246,0.16), transparent 66%)'
};
const ACCENT = {
  blue: 'linear-gradient(135deg, #2563FF, #6D8DFF)',
  violet: 'linear-gradient(135deg, #7A4DFF, #B49CFF)',
  mixed: 'linear-gradient(135deg, #2563FF, #8B5CF6)'
} as const;
function Line({
  w,
  soft = false
}: {
  w: string;
  soft?: boolean;
}) {
  return <span className={`block h-[3px] rounded-full ${soft ? 'bg-ink/[0.06]' : 'bg-ink/[0.10]'}`} style={{
    width: w
  }} data-id="element-202" data-mp="1r5e8w3rig80g" />;
}
function Card({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`rounded-xl border border-ink/[0.07] bg-white/95 shadow-soft backdrop-blur-sm ${className}`} data-id="element-203" data-mp="13e8vdv91lrtb">
      {children}
    </div>;
}

/** Portada editorial de recurso: bloque de color + título, sobre papel blanco. */
function Cover({
  title,
  kicker,
  width,
  accent = 'mixed',
  rotate = 0,
  arc = true
}: {
  title: string;
  kicker: string;
  width: string;
  accent?: keyof typeof ACCENT;
  rotate?: number;
  arc?: boolean;
}) {
  return <div className="relative" style={{
    width,
    transform: `rotate(${rotate}deg)`
  }} data-id="element-204" data-mp="2nwf4z25v5lhn">
      <div className="overflow-hidden rounded-2xl border border-ink/[0.07] bg-white shadow-card" data-id="element-205" data-mp="orouesom7ij2">
        <div className="relative aspect-[3/4] p-[8%]" data-id="element-206" data-mp="jslol60t98a1">
          <div aria-hidden="true" className="relative h-[48%] w-full overflow-hidden rounded-xl" style={{
          background: ACCENT[accent]
        }} data-id="element-207" data-mp="xp7m2fwrkv3x">
            {arc && <>
                <span className="absolute -right-7 -top-9 h-24 w-24 rounded-full border-2 border-white/40" data-id="element-208" data-mp="51uyc2evah91" />
                <span className="absolute -bottom-10 -left-8 h-24 w-24 rounded-full bg-white/15" data-id="element-209" data-mp="xwyfphpx2ex" />
              </>}
            <CDIsotype className="absolute bottom-2.5 left-2.5 h-[13%] w-[13%] min-h-[14px] min-w-[14px] text-white" data-id="element-210" data-mp="a0y4jq6l2til" />
          </div>
          <p className="mt-[7%] text-[7px] font-medium uppercase tracking-[0.2em] text-ink-muted" data-id="element-211" data-mp="3ifg6rb9ufeua">
            {kicker}
          </p>
          <p className="mt-1 text-[11px] font-semibold leading-tight text-ink" data-id="element-212" data-mp="3dfbzmbutrwgq">
            {title}
          </p>
          <div className="mt-2.5 space-y-1.5" data-id="element-213" data-mp="1sxj812l1rwgm">
            <Line w="78%" soft data-id="element-214" data-mp="1xr48d1a452iz" />
            <Line w="52%" soft data-id="element-215" data-mp="1xs70fcj69b0h" />
          </div>
        </div>
      </div>
    </div>;
}

/* ── escenas ───────────────────────────────────────────────────────────── */

function PackScene({
  title,
  kicker
}: {
  title: string;
  kicker: string;
}) {
  return <>
      <Cover title={title} kicker={kicker} width="40%" accent="mixed" data-id="element-216" data-mp="2coel0dyhsega" />

      <Card className="absolute left-[6%] top-[14%] w-[24%] p-2.5" data-id="element-217" data-mp="2goordrq756vo">
        <span aria-hidden="true" className="block h-8 w-full rounded-lg" style={{
        background: ACCENT.blue
      }} data-id="element-218" data-mp="2gmub4qpve2xx" />
        <div className="mt-2 space-y-1.5" data-id="element-219" data-mp="27ngzpsi6r4ak">
          <Line w="80%" data-id="element-220" data-mp="27qd1jrx5md4a" />
          <Line w="55%" soft data-id="element-221" data-mp="raa3taqwzq1r" />
        </div>
      </Card>

      <Card className="absolute right-[6%] top-[20%] w-[22%] p-2.5" data-id="element-222" data-mp="r8fkg0vak5zs">
        <div className="grid grid-cols-3 gap-1" data-id="element-223" data-mp="w9d7orv5rq85">
          {[0, 1, 2, 3, 4, 5].map(i => <span key={i} className="block aspect-square rounded-[5px]" style={{
          background: i % 3 === 0 ? ACCENT.blue : i % 3 === 1 ? ACCENT.violet : 'rgba(11,16,32,0.06)'
        }} data-id="element-224" data-mp="1bj05ij0u4gw9" />)}
        </div>
      </Card>

      <Card className="absolute bottom-[10%] right-[10%] w-[28%] p-2.5" data-id="element-225" data-mp="1uhvxdnp80j6x">
        <div className="space-y-1.5" data-id="element-226" data-mp="1qgagzsdh9mrz">
          {[0, 1, 2].map(i => <div key={i} className="flex items-center gap-1.5" data-id="element-227" data-mp="1qe6dvcnekrsr">
              <span className="h-2.5 w-2.5 rounded-full brand-gradient-bg" data-id="element-228" data-mp="1lfmo115ouvv4" />
              <Line w={i === 1 ? '50%' : '70%'} soft data-id="element-229" data-mp="1gimicrcr3v4j" />
            </div>)}
        </div>
      </Card>
    </>;
}
function ToolkitScene() {
  const tiles: Array<keyof typeof ACCENT | 'plain'> = ['mixed', 'plain', 'blue', 'plain', 'violet', 'plain', 'plain', 'blue', 'mixed'];
  return <>
      <Card className="w-[62%] p-4" data-id="element-230" data-mp="3r5yfjiba6z98">
        <div className="flex items-center gap-2" data-id="element-231" data-mp="3w2ymdvfyg61e">
          <span className="grid h-6 w-6 place-items-center rounded-lg brand-gradient-bg" data-id="element-232" data-mp="3w3hw4ovgbo4c">
            <CDIsotype className="h-3 w-3 text-white" data-id="element-233" data-mp="4vcfzv1uppgu" />
          </span>
          <Line w="42%" data-id="element-234" data-mp="9nvr8ld2g8ih" />
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2" data-id="element-235" data-mp="9oyk2yty8l11">
          {tiles.map((t, i) => <span key={i} className="block aspect-square rounded-lg border border-ink/[0.05]" style={{
          background: t === 'plain' ? 'rgba(11,16,32,0.04)' : ACCENT[t]
        }} data-id="element-236" data-mp="dopdzbervh9s" data-mpi={i} />)}
        </div>
      </Card>

      <Card className="absolute left-[4%] top-[12%] w-[26%] p-2.5" data-id="element-237" data-mp="slq6vi8p9i9u">
        <Line w="70%" data-id="element-238" data-mp="2pftc7ytkdy4c" />
        <span className="mt-2 block h-[3px] w-full rounded-full brand-gradient-bg opacity-80" data-id="element-239" data-mp="2pgmdwldo1xfm" />
        <span className="mt-1.5 block h-[3px] w-[48%] rounded-full bg-ink/[0.06]" data-id="element-240" data-mp="2ue4p6if5ooqs" />
      </Card>

      <Card className="absolute bottom-[10%] right-[5%] w-[24%] p-2.5" data-id="element-241" data-mp="2mdouzwfvu40l">
        <div className="flex items-center gap-1.5" data-id="element-242" data-mp="2hf5be2orh4be">
          {['A', 'B', 'C'].map((k, i) => <React.Fragment key={k} data-id="element-243" data-mp="2wce5331jek6a" data-mpi={i}>
              <span className="h-3 w-3 rounded-full" style={{
            background: i === 1 ? ACCENT.violet : ACCENT.blue
          }} data-id="element-244" data-mp="2w99thklqsm5l" />
              {i < 2 && <span className="h-[1px] w-2.5 rounded-full brand-gradient-bg" data-id="element-245" data-mp="35a80mbrh99se" />}
            </React.Fragment>)}
        </div>
      </Card>
    </>;
}
function TemplatesScene() {
  const cards = [{
    rotate: -11,
    x: '-36%',
    y: '5%',
    z: 10,
    accent: 'blue' as const
  }, {
    rotate: -2,
    x: '-4%',
    y: '-2%',
    z: 20,
    accent: 'mixed' as const
  }, {
    rotate: 9,
    x: '26%',
    y: '7%',
    z: 10,
    accent: 'violet' as const
  }];
  return <>
      {cards.map((c, i) => <div key={i} className="absolute w-[30%]" style={{
      transform: `translate(${c.x}, ${c.y}) rotate(${c.rotate}deg)`,
      zIndex: c.z
    }} data-id="element-246" data-mp="3k2i9hcmjunyc" data-mpi={i}>
          <div className="overflow-hidden rounded-xl border border-ink/[0.07] bg-white shadow-card" data-id="element-247" data-mp="bgu1exclh8ml">
            <div className="aspect-[3/4] p-2.5" data-id="element-248" data-mp="3tqnjx56wv985">
              <span aria-hidden="true" className="block h-[40%] w-full rounded-lg" style={{
            background: ACCENT[c.accent]
          }} data-id="element-249" data-mp="2hgofup9i1x4" />
              <div className="mt-2 space-y-1.5" data-id="element-250" data-mp="3opzu2muf4yfi">
                <Line w="82%" data-id="element-251" data-mp="3oqrfj4uaxfs2" />
                <Line w="58%" soft data-id="element-252" data-mp="3ay1tx1ioqcvh" />
                <Line w="70%" soft data-id="element-253" data-mp="3az31ux4emj8z" />
              </div>
              <div className="mt-2 flex items-center gap-1.5" data-id="element-254" data-mp="3ezn0640prswp">
                <CDIsotype className="h-2.5 w-2.5 text-ink" data-id="element-255" data-mp="3f06fre2w657k" />
                <Line w="38%" soft data-id="element-256" data-mp="30zlywngjejgp" />
              </div>
            </div>
          </div>
        </div>)}

      <Card className="absolute bottom-[8%] left-[8%] z-30 w-[26%] p-2.5" data-id="element-257" data-mp="35xx8cx2x6rye">
        <div className="flex gap-1.5" data-id="element-258" data-mp="3lgq8bk82tpc4">
          {[ACCENT.blue, ACCENT.violet, '#0B1020', 'rgba(11,16,32,0.08)'].map(bg => <span key={bg} className="h-3.5 w-3.5 rounded-full border border-ink/[0.06]" style={{
          background: bg
        }} data-id="element-259" data-mp="3gietsw6u6qnk" />)}
        </div>
      </Card>
    </>;
}
function GuideScene({
  title,
  kicker
}: {
  title: string;
  kicker: string;
}) {
  return <>
      {/* páginas apiladas detrás */}
      <div aria-hidden="true" className="absolute h-[54%] w-[34%] rounded-2xl border border-ink/[0.06] bg-white/70" style={{
      transform: 'translate(14%, 6%) rotate(8deg)'
    }} data-id="element-260" data-mp="2xqcmibg34mpz" />
      <div aria-hidden="true" className="absolute h-[56%] w-[35%] rounded-2xl border border-ink/[0.07] bg-white/85" style={{
      transform: 'translate(6%, 2%) rotate(4deg)'
    }} data-id="element-261" data-mp="2orqza2zo4jiv" />
      <Cover title={title} kicker={kicker} width="38%" accent="violet" rotate={-3} data-id="element-262" data-mp="2etul12bfp8gj" />

      <Card className="absolute left-[5%] top-[16%] w-[27%] p-2.5" data-id="element-263" data-mp="uclfnlycaj09">
        <span className="text-[7px] font-medium uppercase tracking-[0.18em] text-ink-muted" data-id="element-264" data-mp="z8ah5utx69z7">
          Contenido
        </span>
        <div className="mt-2 space-y-1.5" data-id="element-265" data-mp="146kcq6jk3clc">
          {[0, 1, 2, 3].map(i => <div key={i} className="flex items-center gap-1.5" data-id="element-266" data-mp="189h34jfc8czu">
              <span className={`h-1.5 w-1.5 rounded-full ${i === 0 ? 'brand-gradient-bg' : 'bg-ink/15'}`} data-id="element-267" data-mp="bksozy6qt9vp" />
              <Line w={i === 0 ? '72%' : '54%'} soft data-id="element-268" data-mp="gjc8zt1s0blf" />
            </div>)}
        </div>
      </Card>

      <svg aria-hidden="true" viewBox="0 0 100 100" className="absolute right-[4%] bottom-[12%] h-[30%] w-[26%]" data-id="element-269" data-mp="kil3x18jyfik">
        <defs data-id="element-270" data-mp="3ozpajxh20rkq">
          <linearGradient id="guideArc" x1="0" y1="1" x2="1" y2="0" data-id="element-271" data-mp="3oy4f85mx14lt">
            <stop offset="0%" stopColor="#2563FF" data-id="element-272" data-mp="3szzel4lad7wy" />
            <stop offset="100%" stopColor="#8B5CF6" data-id="element-273" data-mp="3syod5bfbcc90" />
          </linearGradient>
        </defs>
        <g fill="none" stroke="url(#guideArc)" strokeLinecap="round" data-id="element-274" data-mp="2w25gwypi9jbh">
          <path d="M12 82 A 58 58 0 0 1 84 20" strokeWidth="6" opacity="0.28" data-id="element-275" data-mp="310p0isgmmj0o" />
          <path d="M28 88 A 46 46 0 0 1 88 40" strokeWidth="2" opacity="0.5" data-id="element-276" data-mp="352tsznvc99lq" />
        </g>
      </svg>
    </>;
}

/* ── portada ───────────────────────────────────────────────────────────── */

export function ProductCover({
  title,
  kicker,
  scene,
  variant = 'card'
}: ProductCoverProps) {
  return <div className="relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden bg-paper-alt" role="img" aria-label={`Portada del recurso digital ${title}`} data-id="element-277" data-mp="2hczgx8iufp2j">
      <div aria-hidden="true" className="absolute inset-0" style={{
      background: SCENE_TINT[scene]
    }} data-id="element-278" data-mp="2r3u2sbs6gmng" />
      <div aria-hidden="true" className="absolute inset-0 grid-lines opacity-50 [mask-image:radial-gradient(80%_80%_at_50%_50%,black,transparent_78%)]" data-id="element-279" data-mp="1tnn7q8ninh08" />

      {scene === 'pack' && <PackScene title={title} kicker={kicker} data-id="element-280" data-mp="1r38hxjksxawc" />}
      {scene === 'toolkit' && <ToolkitScene data-id="element-281" data-mp="1m5p3ojo3sy4e" />}
      {scene === 'templates' && <TemplatesScene data-id="element-282" data-mp="1m2uj8s53bdaz" />}
      {scene === 'guide' && <GuideScene title={title} kicker={kicker} data-id="element-283" data-mp="1h4sxu0k6z1wc" />}

      {variant === 'featured' && <div aria-hidden="true" className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-ink/[0.05]" data-id="element-284" data-mp="1d4ims9hjllam" />}
    </div>;
}